// ClientHub Conversations data layer (spec §4 — /hub/conversations).
//
// Modelled on api/now.js (the closest analog: a page-level data layer with
// HUB_STUB fixture fallback + one jittered transport retry + gate-envelope
// unwrap). View reads (v_send_*) follow api/shell.js. Components never touch
// supabase — every read/write lives here (00_CONTRACT.md §11).
//
// fn_compose_send is the ONLY message write path, and it is ALREADY wired through
// <Composer> (api/hub.js → composeSend). This page does NOT re-import it — it
// renders <Composer>, which writes through the single path. The full
// fn_compose_send signature (p_client_facing, p_reply_to, p_schedule_at,
// p_as_agent) is a kit gap on <Composer> itself — flag for Charles, never fork.
//
// PROBE STATUS (VERIFIED — 2026-09-07): the fn_conversation_* / fn_thread_* /
// fn_route_inbound / v_send_* / v_routing_* names below are confirmed to EXIST
// against live Supabase (project jqmnhmoulybkggahkmzz) by
// scripts/probe-conversations.js — all 9 Conversations RPCs + fn_compose_send
// resolved, probed READ-ONLY (the write RPCs refused a zero-uuid arg, proving
// existence without mutating a real row; fn_send_pause / fn_send_resume were
// SKIPPED — they are the admin kill switch and would toggle sending for real).
// Return SHAPES still need a signed-in (platform-admin) pass to capture the
// exact JSON; until then the wrappers follow the spec's declared contract and
// degrade through <ErrorState>. ONE backend bug to report (do NOT patch in the
// UI — knowledge-base §18): v_send_health EXISTS but times out (Postgres 57014
// statement timeout); getSendHealth degrades to { enabled: false } until it is
// fixed. Per-object verdicts in docs/CLIENTHUB-RPC-GAPS.md §6.

import { supabase } from "../lib/supabase";
import { dedupe, rpcKey } from "./rpcCache";
import fnRooms from "../components/hub/conversations/__fixtures__/fn_conversation_rooms.json";
import fnThreadsOpen from "../components/hub/conversations/__fixtures__/fn_conversation_threads.open.json";
import fnReadChannel from "../components/hub/conversations/__fixtures__/fn_conversation_read.channel.json";
import fnReadDm from "../components/hub/conversations/__fixtures__/fn_conversation_read.dm.json";
import fnReadThread from "../components/hub/conversations/__fixtures__/fn_conversation_read.thread.json";
import vSendHealth from "../components/hub/conversations/__fixtures__/v_send_health.json";
import fnMembers from "../components/hub/conversations/__fixtures__/ops_team_members.json";
import opsMailboxes from "../components/hub/conversations/__fixtures__/ops_mailboxes.json";
import vMailboxHealth from "../components/hub/conversations/__fixtures__/v_mailbox_health.json";
import vMailboxChunks from "../components/hub/conversations/__fixtures__/v_mailbox_chunks.json";

const HUB_STUB =
  typeof process !== "undefined" &&
  !!process.env &&
  process.env.REACT_APP_HUB_STUB === "1";

// A refused HTTP/2 stream is a transport failure, not a database one — retry once.
// A Postgres logic error / RLS refusal / bad parameter carries a `code` and a 4xx
// HTTP status, and fails on the first attempt, where it belongs. (Mirrors api/now.js.)
//
// EXCEPTION — HTTP 5xx: fn_conversation_rooms returns a transient 500 Internal
// Server Error on the cold first call (Postgres cold cache / statement timeout —
// the same class of issue that times out v_send_health, knowledge-base §18). The
// next call succeeds because the aborted scan paged the function's working set
// into the shared buffer cache. A single aborted scan only PARTIALLY warms it,
// though, so one retry isn't always enough on the cold first page load — this is
// exactly why the manual "retry" button on the error state always works on the
// first tap (by then more of the buffer cache is warm). A 5xx is a server-side
// failure, not a client or logic one, so retry it WITH BACKOFF (up to MAX_RETRIES
// extra attempts) — same treatment as a transport failure. A 4xx (RLS 403,
// bad-param 400) is permanent for this request and is NOT retried.
const TRANSPORT_FAILURE =
  /failed to fetch|networkerror|load failed|err_http2|connection closed/i;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const isTransportFailure = (err) =>
  !err?.code && TRANSPORT_FAILURE.test(err?.message || "");
// Supabase v2 PostgrestError carries a numeric `status` (the HTTP status code).
// 5xx = server-side / transient; 4xx = client-side / permanent for this request.
const isTransientServerError = (err) =>
  err && typeof err.status === "number" && err.status >= 500;
// Postgres statement timeout (57014) is the cold-cache victim on
// fn_conversation_rooms. PostgREST normally surfaces it as HTTP 500 (so
// isTransientServerError catches it), but on some Supabase pooler/edge
// configurations the error reaches the client WITHOUT a numeric `status`.
// Match the Postgres code + message explicitly so the cold-first-load 57014 is
// ALWAYS retried regardless of how it was wrapped — otherwise the room breaks
// and the user has to tap the retry button by hand.
const isStatementTimeout = (err) =>
  !!err &&
  /57014|canceling statement|statement timeout/i.test(
    `${err.code || ""} ${err.message || ""}`,
  );
const shouldRetry = (err) =>
  isTransportFailure(err) ||
  isTransientServerError(err) ||
  isStatementTimeout(err);

// Retry transient failures (transport OR 5xx) up to MAX_RETRIES extra times with
// backoff. The critical case is fn_conversation_rooms on the cold first page
// load: it 500s with Postgres 57014 "canceling statement due to statement
// timeout" because the function's working set isn't in shared_buffers yet, so
// the cold execution exceeds statement_timeout and Postgres aborts it. Each
// aborted scan still pages data into the shared buffer cache, so a later attempt
// runs against warmer buffers and succeeds. The backoff gives the DB room to
// recycle the aborted backend and lets successive attempts build up enough warm
// buffers to get under the timeout — automating the "tap the retry button"
// recovery the user otherwise has to do by hand.
const MAX_RETRIES = 2;
const RETRY_DELAYS = [350, 1200]; // ms; one per extra attempt, repeats the last
// fn_conversation_rooms is the known cold-cache victim: the first call after
// idle can 57014 (statement timeout) because its working set isn't in
// shared_buffers yet. Each aborted scan still pages data into the buffer cache,
// so a later attempt runs against warmer buffers and succeeds — this is exactly
// why the manual "retry" button on the error state always works on the first
// tap. We give rooms a LARGER retry budget than the default so the automated
// recovery REPLACES the manual tap: the user never sees a broken room, only a
// (sometimes slow) first load. This is a STOPGAP — the real fix is DB-side
// indexes (see docs/CONVERSATION-ROOMS-FIRST-LOAD-TIMEOUT.md). The extra
// attempts only fire on actual 57014s, so the warm path pays nothing.
const ROOMS_MAX_RETRIES = 4; // 1 initial + 4 retries = 5 total attempts

// Shared RPC call: retry transient failures once (transport OR 5xx), surface the
// gate envelope. fn_* RPCs gate themselves and return {error:true, message}
// rather than throwing — surface it as a real error (mirrors api/now.js nowRpc).
//
// SESSION GATE: the Supabase JS client restores the session from localStorage
// asynchronously on creation. Without waiting, the FIRST RPC on a fresh page
// load fires before the session is ready → it goes out with no bearer token and
// fails (401/403 or PGRST), while the sequentially-deferred second call fires
// after the session is restored and succeeds. This is exactly the "dms + rooms
// always fail, threads don't" race on /hub/conversations: fn_conversation_rooms
// is the first call (fails), fn_conversation_threads is the second (succeeds).
// Awaiting getSession() resolves initializePromise first, so every RPC carries
// the user's token. Cheap when the session is already loaded (no extra I/O).
async function convRpc(name, params, label, { retries = MAX_RETRIES } = {}) {
  if (HUB_STUB) return stub(name, params);
  await supabase.auth.getSession();
  const attempt = async () => {
    try {
      return await supabase.rpc(name, params);
    } catch (e) {
      return { data: null, error: e };
    }
  };
  let result = await attempt();
  for (let i = 0; i < retries && shouldRetry(result.error); i++) {
    await sleep(RETRY_DELAYS[i] ?? RETRY_DELAYS[RETRY_DELAYS.length - 1]);
    result = await attempt();
  }
  const { data, error } = result;
  if (error) {
    const detail = error?.message || "Supabase RPC failed";
    throw new Error(label ? `${label}: ${detail}` : detail);
  }
  if (data && typeof data === "object" && data.error === true) {
    const detail = data.message || `${name} returned an error`;
    throw new Error(label ? `${label}: ${detail}` : detail);
  }
  return data;
}

// View read (mirrors api/shell.js). The wall is IN the view (RLS) — a UI mistake
// cannot leak a DM. Throws+labels on a Postgres error so the surface degrades
// through <ErrorState>, never a silent empty.
async function readView(view, { label, order = "at", limit = 500 } = {}) {
  if (HUB_STUB) return stub(view, {});
  const { data, error } = await supabase
    .from(view)
    .select("*")
    .order(order, { ascending: false })
    .limit(limit);
  if (error) throw new Error(label ? `${label}: ${error.message}` : error.message);
  return Array.isArray(data) ? data : [];
}

// ── Rooms (spec §4) ──────────────────────────────────────────────────────────
// fn_conversation_rooms(p_client_id, p_include_quiet) →
//   { channels[], dms[], counts{ channels, dms, open_threads } }
// Channel: id · name · client_id · client · client_facing · company_key ·
//          threads · messages · last_at
// DM: id · with · messages · first_at · last_at   (rendered by person, not id)
export async function getConversationRooms({
  clientId = null,
  includeQuiet = false,
} = {}) {
  const params = { p_client_id: clientId, p_include_quiet: includeQuiet };
  return dedupe(rpcKey("fn_conversation_rooms", params), () =>
    convRpc("fn_conversation_rooms", params, "conversation rooms", {
      retries: ROOMS_MAX_RETRIES,
    }),
  );
}

// ── Counts (the open-threads badge number) ──────────────────────────────────
// fn_conversation_counts(p_client_id) → { channels, dms, open_threads }
// Count-only RPC — the sidebar badge uses the same one. The Conversations page
// polls this so its page-header and Threads-section numbers stay as fresh as the
// sidebar badge, WITHOUT re-running the heavy fn_conversation_rooms scan (the
// 57014-prone one) just for a count. p_client_id scopes to one client; the global
// page passes none (same as the sidebar badge).
export async function getConversationCounts({ clientId = null } = {}) {
  const params = {};
  if (clientId) params.p_client_id = clientId;
  // Deduped in-flight with the sidebar badge's fn_conversation_counts call
  // (same key — no clientId on either — so the shell + the Conversations page
  // mount burst is ONE request, not two; perf memo §2 rule 5).
  return dedupe(rpcKey("fn_conversation_counts", params), () =>
    convRpc("fn_conversation_counts", params, "conversation counts"),
  );
}

// ── Threads (spec §4) ────────────────────────────────────────────────────────
// fn_conversation_threads(p_scope, p_client_id, p_channel, p_search, p_limit, p_before) →
//   { scope, items[], counts{ open, mine, unassigned, total }, next_before }
// Item: id · subject · summary · channel · client_id · client · client_facing ·
//       assigned_to · assigned · messages · last_at · resolved · urgency ·
//       sentiment · awaiting_us · has_commitment
//
// scope defaults to 'open' — build around open, not mine (assigned is null on
// almost everything). Paginate with next_before (969 open → load 100, then more).
export async function getConversationThreads({
  scope = "open",
  clientId = null,
  channel = null,
  search = null,
  limit = 100,
  before = null,
} = {}) {
  const params = { p_scope: scope, p_limit: limit };
  if (clientId) params.p_client_id = clientId;
  if (channel) params.p_channel = channel;
  if (search) params.p_search = search;
  if (before) params.p_before = before;
  return dedupe(rpcKey("fn_conversation_threads", params), () =>
    convRpc("fn_conversation_threads", params, "conversation threads"),
  );
}

// ── Read one (spec §4) ───────────────────────────────────────────────────────
// fn_conversation_read(p_type, p_id, p_limit, p_before) →
//   channel → { type, channel, messages[] }
//   thread  → { type, thread, messages[], work[], commitments[] }
// Paginate backwards with p_before (the largest DM is 12,989 messages — never
// fetch a whole conversation).
export async function readConversation({
  type,
  id,
  limit = 60,
  before = null,
} = {}) {
  if (!type || !id) throw new Error("readConversation: type and id are required");
  const params = { p_type: type, p_id: id, p_limit: limit };
  if (before) params.p_before = before;
  return dedupe(rpcKey("fn_conversation_read", params), () =>
    convRpc("fn_conversation_read", params, "conversation read"),
  );
}

// ── Thread lifecycle (spec §4) ───────────────────────────────────────────────
// Writes — never retried. The DB refuses these on a DM/channel:
//   fn_thread_assign on a DM → "a DM has no owner — it is a conversation with a person"
//   fn_thread_resolve on a DM/channel → "channels and DMs are not resolvable — they are ongoing"
// The UI never offers the control for those kinds (Epic C2), so a human error
// can't reach the guard — but the guard still refuses if it does.
export async function assignThread(threadId, ownerId) {
  return convRpc(
    "fn_thread_assign",
    { p_thread_id: threadId, p_owner_id: ownerId },
    "thread assign",
  );
}
export async function resolveThread(threadId, resolved = true) {
  return convRpc(
    "fn_thread_resolve",
    { p_thread_id: threadId, p_resolved: resolved },
    "thread resolve",
  );
}

// ── Assignable members (spec §4 — thread assign picker, Epic C3) ──────────────
// The member-select picker lists who a thread can be assigned to. The source is
// ops_team_members — one table for humans + agents (knowledge-base §5; also
// named in the §0 probe's set_config block). VERIFIED 2026-09-14 (Team & Admin
// backend answers): the table has 52 rows — 19 current humans, 1 agent (the AI
// Enrichment Worker, member_type='agent'), and 32 former people
// (is_current=false, no login). An unfiltered read lists everyone who ever
// left plus the AI worker, so the query MUST filter where is_current AND
// member_type='human' — never build a picker off an unfiltered list. We select
// id + name and order by name; a missing column or an RLS refusal throws a
// labelled error so the picker degrades through <ErrorState> (never a silent
// empty — knowledge-base §18).
export async function getAssignableMembers() {
  if (HUB_STUB) return stub("ops_team_members", {});
  const { data, error } = await supabase
    .from("ops_team_members")
    .select("id, name")
    .eq("is_current", true)
    .eq("member_type", "human")
    .order("name", { ascending: true });
  if (error) throw new Error(`assignable members: ${error.message}`);
  return Array.isArray(data) ? data : [];
}



// ── Routing (spec §4.1) ───────────────────────────────────────────────────────
// An explicit rule wins. Then evidence from 102,000 completed tasks. Then the
// relationship + capability graphs. Always a suggestion with a reason, never a
// silent assignment — a wrong auto-assign is worse than none because nobody knows
// to look for it.
export async function routeInbound(messageId) {
  return convRpc("fn_route_inbound", { p_message_id: messageId }, "route inbound");
}
export async function getRoutingSuggestions() {
  return readView("v_routing_suggestions", {
    label: "routing suggestions",
    order: "term",
  });
}
export async function acceptRoutingRule({ term, memberId, note = null }) {
  return convRpc(
    "fn_routing_rule_accept",
    { p_term: term, p_member_id: memberId, p_note: note },
    "routing rule accept",
  );
}

// ── Routing rules (spec §4.1 — Epic 2: hide Accept once a rule exists) ─────────
// `ops_routing_rules` is a staff-readable config table. The per-thread composer
// suggestion comes from `routeInbound` (not `v_routing_suggestions`, which joins
// these), so the composer has no rule list of its own — this gives it one. We
// select the `term` column ONLY (nothing else on the screen needs the other
// columns), cache it for the page session (it changes rarely), and return a
// `Set<string>` for O(1) "is this term already a rule?" lookups. Mirrors the
// readView wrapper shape: throws+labels on a Postgres error so the surface
// degrades through <ErrorState>, never a silent empty (knowledge-base §18). A
// failure throws — the page treats it as "no rules known" (Accept stays
// visible) rather than blocking the composer, because hiding the button is a
// nicety, not a gate; the DB function still refuses a true duplicate.
export async function getRoutingRules() {
  if (HUB_STUB) {
    // A couple of learned terms so the hide-Accept behavior (Epic 2) is
    // observable in HUB_STUB mode. (The rule set now comes from
    // v_routing_suggestions, not ops_routing_rules — see the live branch.)
    return new Set(["homepage", "invoice"]);
  }
  // `term` is NOT a column on ops_routing_rules (its columns are id, match_kind,
  // match_value, assign_to, assign_capability, priority, note, is_active,
  // created_at) — selecting `term` there 400s twice per load. `term` lives on
  // v_routing_suggestions, which already joins ops_routing_rules and exposes an
  // `already_a_rule` flag, so read the rule set from that view (perf memo §3).
  // Deduped in-flight so StrictMode's double-invoke (and any concurrent mount)
  // produces one request, not two — the general fix, not a StrictMode special-case.
  return dedupe("v_routing_suggestions:rules", async () => {
    const { data, error } = await supabase
      .from("v_routing_suggestions")
      .select("term")
      .eq("already_a_rule", true);
    if (error) throw new Error(`routing rules: ${error.message}`);
    return new Set(Array.isArray(data) ? data.map((r) => r && r.term) : []);
  });
}

// ── Send pipeline (spec §4.3) ────────────────────────────────────────────────
// fn_compose_send does NOT send — it stores the message as approved. A worker
// (the gmail-send edge function) claims it via fn_send_claim and delivers it.
// fn_send_allowed / fn_send_claim are OUT OF SCOPE for this page — they run in
// the edge worker, not in React. The UI reads the health views and surfaces the
// blocked reason; it never sends directly.
//
// v_send_health → { enabled, sent_this_hour, blocked_today, waiting } (one row).
//   enabled is FALSE by default; a watchdog trips it on a volume spike. Show
//   "sending is off" BEFORE the send, not as a post-send error.
export async function getSendHealth() {
  const rows = await readView("v_send_health", { label: "send health", limit: 1 });
  return Array.isArray(rows) && rows.length ? rows[0] : { enabled: false };
}
// v_send_blocked → what was stopped, and WHY (shown on the returned-to-draft message).
export async function getSendBlocked() {
  return readView("v_send_blocked", { label: "send blocked" });
}
// v_composer_pending → per message: why it has not gone out yet.
export async function getComposerPending() {
  return readView("v_composer_pending", { label: "composer pending" });
}
// Kill switch (admin, §4.3). p_reason is REQUIRED (no default — "why" is the first
// question the next person asks; written to ops_send_config.paused_reason and
// surfaced by v_send_health). Throw client-side on a missing reason so the error
// is clear before the network call. D2 wires the admin surface at /hub/admin/send;
// the UI-facing pieces (send-off notice, blocked reason) ship in C4 and only READ.
// Writes are never retried — a transport failure surfaces a real error and the
// admin re-attempts consciously.
export async function pauseSending(reason) {
  if (!reason) throw new Error("pauseSending: p_reason is required");
  return convRpc("fn_send_pause", { p_reason: reason }, "send pause");
}
export async function resumeSending() {
  return convRpc("fn_send_resume", {}, "send resume");
}

// ── Mailbox health (spec §4.2b — admin surface, Epic C5 + D1) ─────────────────
// An ADMIN surface, not a page-affecting feature. ops_mailboxes is the on/off +
// rule per mailbox; v_mailbox_health is the last sync / errors / counts per
// mailbox; v_mailbox_chunks is the backfill progress.
//
// VERIFIED 2026-09-09 (live PostgREST OpenAPI spec — project
// jqmnhmoulybkggahkmzz). A mailbox keys on its EMAIL ADDRESS, not a uuid —
// ops_mailboxes has no surrogate key worth passing around. The live signatures:
//   fn_mailbox_enable (p_email text, p_note text default null) → jsonb
//   fn_mailbox_disable(p_email text, p_reason text)             — p_reason REQUIRED
//
// fn_mailbox_enable returns the Gmail query it will run (a long list of
// from:/to: terms for every client domain) — the page shows it to the admin
// so they can see what a personal mailbox is about to ingest (§C-1).
//
// Writes are never retried (they are writes), and gated by the agent.manage
// capability on the page (the closest existing admin capability; a backend
// mailbox.manage capability is the eventual right gate — see
// docs/CLIENTHUB-RPC-GAPS.md §7).
export async function getMailboxes() {
  return readView("ops_mailboxes", { label: "mailboxes", order: "email" });
}
export async function getMailboxHealth() {
  return readView("v_mailbox_health", { label: "mailbox health" });
}
export async function getMailboxChunks() {
  return readView("v_mailbox_chunks", { label: "mailbox chunks" });
}

// Returns the Gmail query the mailbox will ingest (show it to the admin — §C-1).
export async function enableMailbox(email, note = null) {
  return convRpc(
    "fn_mailbox_enable",
    { p_email: email, p_note: note },
    "mailbox enable",
  );
}

// p_reason is REQUIRED (no default, server-side enforced). Throw client-side
// on a missing reason so the error is clear before the network call.
export async function disableMailbox(email, reason) {
  if (!reason) throw new Error("disableMailbox: p_reason is required");
  return convRpc(
    "fn_mailbox_disable",
    { p_email: email, p_reason: reason },
    "mailbox disable",
  );
}

// ── Stub (local dev only — HUB_STUB=1) ───────────────────────────────────────
// Until the §0 probe confirms shapes, the stub returns the fixture payloads for
// reads and {ok:true} for writes. The fixtures are SPEC-DERIVED (from the spec's
// declared shapes + the expected counts), NOT probed — see the fixture README.
function stub(name, params) {
  if (name === "fn_conversation_rooms") return fnRooms;
  if (name === "fn_conversation_threads") {
    if (params?.p_scope === "open") return fnThreadsOpen;
    return {
      scope: params?.p_scope || "open",
      items: [],
      counts: { open: 0, mine: 0, unassigned: 0, total: 0 },
      next_before: null,
    };
  }
  if (name === "fn_conversation_read") {
    if (params?.p_type === "thread") return fnReadThread;
    if (params?.p_type === "dm") return fnReadDm;
    return fnReadChannel;
  }
  if (
    name === "fn_thread_assign" ||
    name === "fn_thread_resolve" ||
    name === "fn_route_inbound" ||
    name === "fn_routing_rule_accept" ||
    name === "fn_send_pause" ||
    name === "fn_send_resume"
  ) {
    return { ok: true };
  }
  if (name === "v_send_health") return [vSendHealth];
  if (name === "v_send_blocked" || name === "v_routing_suggestions") return [];
  if (name === "v_composer_pending") return [];
  if (name === "ops_team_members") return fnMembers;
  if (name === "ops_routing_rules") {
    // A couple of learned terms so the hide-Accept behavior (Epic 2) is
    // observable in HUB_STUB mode. Real reads select `term` only; the stub
    // returns the row shape getRoutingRules maps over.
    return [
      { term: "homepage" },
      { term: "invoice" },
    ];
  }
  if (name === "ops_mailboxes") return opsMailboxes;
  if (name === "v_mailbox_health") return vMailboxHealth;
  if (name === "v_mailbox_chunks") return vMailboxChunks;
  if (name === "fn_mailbox_enable") {
    return {
      ok: true,
      gmail_query:
        "from:verderanch.com OR to:verderanch.com OR from:lakesideglamping.com OR to:lakesideglamping.com",
    };
  }
  if (name === "fn_mailbox_disable") {
    return { ok: true };
  }
  return null;
}

